import EmptyState from '@/components/EmptyState';
import { useT } from '@/i18n';
import { fromApi } from '@/utils/datetime';
import type { Reservation } from '@restaurant/api-client';
import { Badge, Block, List, ListItem, Navbar, Page, Preloader, f7 } from 'framework7-react';
import { useExpectedArrivals, useSeatReservation } from '../hooks/useTableOrdering';

/**
 * Today's due bookings, soonest first - seat one in a single tap (opens its
 * tab + attaches the reservation + marks arrived). Overdue-unseated bookings
 * are flagged so staff catch a no-show risk before the sweep does.
 */
export default function ExpectedArrivalsPage() {
  const tAll = useT();
  const t = tAll.tableOrdering.arrivals;
  const tAttach = tAll.tableOrdering.attachReservation;
  const tWorkspace = tAll.tableOrdering.workspace;
  const { arrivals, isLoading } = useExpectedArrivals();
  const seat = useSeatReservation();

  const seatGuest = (r: Reservation) => {
    f7.dialog.confirm(t.seatConfirm(r.guest.name, r.partySize), t.seatTitle, () => {
      seat.mutate(
        { tableId: r.tableIds[0], reservationId: r.id },
        {
          onSuccess: (s) => {
            f7.toast.show({ text: t.seatedToast(r.guest.name), closeTimeout: 2000 });
            f7.views.current.router.navigate(`/tables/session/${s.id}`);
          },
        },
      );
    });
  };

  return (
    <Page name="expected-arrivals">
      <Navbar title={t.title} backLink={tWorkspace.tables} />
      {isLoading ? (
        <Block style={{ textAlign: 'center', paddingTop: 48 }}>
          <Preloader size={32} />
        </Block>
      ) : arrivals.length === 0 ? (
        <Block style={{ paddingTop: 32 }}>
          <EmptyState icon="person_2" title={t.noArrivals} description={t.noArrivalsDesc} />
        </Block>
      ) : (
        <List dividers strongIos insetIos style={{ marginTop: 16 }}>
          {arrivals.map((r) => {
            const time = fromApi(r.time);
            const overdue = time.toMillis() < Date.now();
            return (
              <ListItem
                key={r.id}
                link="#"
                title={r.guest.name}
                footer={tAttach.partyOf(r.partySize)}
                onClick={() => seatGuest(r)}
              >
                <div slot="after" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {overdue && <Badge color="red">{t.overdue}</Badge>}
                  <span style={{ fontWeight: 600, color: 'var(--f7-text-color)' }}>
                    {time.toFormat('HH:mm')}
                  </span>
                </div>
              </ListItem>
            );
          })}
        </List>
      )}
    </Page>
  );
}
